import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

/**
 * Modal de confirmação de exclusão de parceria
 * Mostra informações da parceria e pede confirmação
 */
export default function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  partnership,
  isDeleting
}) {
  useBodyScrollLock(isOpen);
  if (!isOpen || !partnership) return null;

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 shadow-2xl border-2 border-red-500">
        <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            Confirmar Exclusão
          </CardTitle>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          {/* Aviso */}
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <AlertDescription className="text-red-800 ml-2">
              <strong>Atenção!</strong> Esta ação não pode ser desfeita.
            </AlertDescription>
          </Alert>

          {/* Informações da Parceria */}
          <div className="space-y-3">
            <p className="text-gray-700">
              Você está prestes a excluir a seguinte parceria:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 border border-gray-200">
              <div>
                <span className="text-sm font-semibold text-gray-600">OSC:</span>
                <p className="text-gray-900 font-medium">{partnership.osc}</p>
              </div>
              
              {partnership.cnpj && (
                <div>
                  <span className="text-sm font-semibold text-gray-600">CNPJ:</span>
                  <p className="text-gray-900">{partnership.cnpj}</p>
                </div>
              )}
              
              {partnership.processo_ano && (
                <div>
                  <span className="text-sm font-semibold text-gray-600">Processo/Ano:</span>
                  <p className="text-gray-900">{partnership.processo_ano}</p>
                </div>
              )}
              
              {partnership.valor && (
                <div>
                  <span className="text-sm font-semibold text-gray-600">Valor:</span>
                  <p className="text-gray-900">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(partnership.valor)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Pergunta de Confirmação */}
          <div className="text-center py-2">
            <p className="text-lg font-semibold text-gray-900">
              Tem certeza que deseja excluir?
            </p>
          </div>

          {/* Botões */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sim, Excluir
                </>
              )}
            </Button>
          </div>

          {/* Info adicional */}
          <p className="text-xs text-center text-gray-500">
            Os dados serão permanentemente removidos do sistema
          </p>
        </CardContent>
      </Card>
    </div>,
    document.body
  );
}
